import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Package, PlusCircle, LogOut, Trash2, ClipboardList, ChefHat, Edit2, DollarSign, ShoppingCart, Upload, Image, X, Check, Truck, Settings, Mail, Phone, RefreshCw, RotateCcw, Users, Star, Menu as MenuIcon, MessageSquare, Key } from 'lucide-react';
import PaymentConfigForm from '../components/PaymentConfigForm';
import EmailConfigForm from '../components/EmailConfigForm';
import AlertConfigForm from '../components/AlertConfigForm';
import FinanceDashboard from './FinanceDashboard';

// Always normalise image URLs to full URLs (e.g. https://nayanskitchen.com/api/uploads/menu-xxx.jpg or http://localhost:5021/api/uploads/...)
// /api/uploads/ is proxied by Nginx → backend → express.static('uploads')
const toFullImageUrl = (image) => {
    if (!image) return '';
    
    // External image links not uploaded to our backend
    if (image.startsWith('http') && !image.includes('/uploads/')) {
        return image;
    }
    
    const idx = image.indexOf('/uploads/');
    if (idx !== -1) {
        const filename = image.substring(idx + 9);
        const apiPath = `/api/uploads/${filename}`;
        
        const backendUrl = process.env.REACT_APP_BACKEND_URL || (window.location.protocol + '//' + window.location.host);
        const cleanBackend = backendUrl.replace(/\/$/, '');
        return `${cleanBackend}${apiPath}`;
    }
    
    return image;
};

const resolveImageUrl = (image) => {
    if (!image) return '';
    return toFullImageUrl(image);
};


const AdminDashboard = () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    console.log(API_URL);
    const [orders, setOrders] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [newItem, setNewItem] = useState({ name: '', price: '', description: '', shortDescription: '', image: '', category: 'Main', minQty: 1 });
    const [activeTab, setActiveTab] = useState('orders');
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const editFormRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [uploading, setUploading] = useState(false);

    // New Data States
    const [customers, setCustomers] = useState([]);
    const [supportMessages, setSupportMessages] = useState([]);
    const [feedbacks, setFeedbacks] = useState([]);
    const [minOrderValue, setMinOrderValue] = useState(0);
    const [minOrderInput, setMinOrderInput] = useState('');
    const [minOrderSaving, setMinOrderSaving] = useState(false);
    const [minOrderSaved, setMinOrderSaved] = useState(false);
    const navigate = useNavigate();
    const token = localStorage.getItem('adminToken');

    useEffect(() => {
        if (!token) {
            navigate('/admin-login');
            return;
        }
        fetchData();
    }, []);

    // `silent` is used by the auto-refresh poll: it skips the loading spinner so
    // the table doesn't flash, and leaves form inputs alone so it can't overwrite
    // whatever the admin is typing.
    const fetchData = async ({ silent = false } = {}) => {
        if (!token) return;
        if (!silent) setLoading(true);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const [ordersRes, menuRes, customersRes, messagesRes, feedbacksRes, settingsRes] = await Promise.all([
                axios.get(`${API_URL}/api/admin/orders`, config),
                axios.get(`${API_URL}/api/public/menu`),
                axios.get(`${API_URL}/api/admin/customers`, config),
                axios.get(`${API_URL}/api/admin/support-messages`, config),
                axios.get(`${API_URL}/api/admin/feedbacks`, config),
                axios.get(`${API_URL}/api/admin/settings`, config)
            ]);

            setOrders(ordersRes.data);
            setMenuItems(menuRes.data);
            setCustomers(customersRes.data);
            setSupportMessages(messagesRes.data);
            setFeedbacks(feedbacksRes.data);
            setMinOrderValue(settingsRes.data.minOrderValue || 0);
            if (!silent) setMinOrderInput(String(settingsRes.data.minOrderValue || 0));
            setLastUpdated(new Date());
        } catch (err) {
            console.error("Data fetch failed", err);
            if (err.response?.status === 401 || err.response?.status === 403) {
                localStorage.removeItem('adminToken');
                navigate('/admin-login');
            }
        } finally {
            if (!silent) setLoading(false);
        }
    };

    // Keep the live queue current without the kitchen having to hit refresh.
    // Pauses while the tab is in the background so it isn't polling all night.
    useEffect(() => {
        if (activeTab !== 'orders' || !token) return;
        const interval = setInterval(() => {
            if (document.visibilityState === 'visible') fetchData({ silent: true });
        }, 30000);
        return () => clearInterval(interval);
    }, [activeTab, token]);

    const handleImageFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadImage = async () => {
        if (!imageFile) return null;

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('image', imageFile);

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            };

            const res = await axios.post(`${API_URL}/api/admin/upload-image`, formData, config);
            const fullUrl = toFullImageUrl(res.data.imageUrl);
            setNewItem(prev => ({ ...prev, image: fullUrl }));
            return fullUrl;
        } catch (err) {
            console.error("Image upload failed", err);
            alert("Failed to upload image");
            return null;
        } finally {
            setUploading(false);
        }
    };

    const handleAddFood = async (e) => {
        e.preventDefault();
        try {
            let imageUrl = newItem.image;

            // If user selected a file, upload it first
            if (imageFile) {
                const uploadedUrl = await uploadImage();
                if (uploadedUrl) {
                    imageUrl = uploadedUrl;
                } else {
                    return; // Stop if upload failed
                }
            }

            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.post(`${API_URL}/api/admin/menu`, { ...newItem, image: toFullImageUrl(imageUrl) }, config);
            setNewItem({ name: '', price: '', description: '', shortDescription: '', image: '', category: 'Main', minQty: 1 });
            setImageFile(null);
            setImagePreview('');
            fetchData();
            alert("Item added to menu successfully!");
        } catch (err) {
            alert("Failed to add item");
        }
    };

    const handleSaveMinOrderValue = async () => {
        setMinOrderSaving(true);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const res = await axios.put(`${API_URL}/api/admin/settings`, { minOrderValue: Number(minOrderInput) || 0 }, config);
            setMinOrderValue(res.data.minOrderValue);
            setMinOrderSaved(true);
            setTimeout(() => setMinOrderSaved(false), 2500);
        } catch (err) {
            alert('Failed to save settings');
        } finally {
            setMinOrderSaving(false);
        }
    };

    const handleDeleteItem = async (id) => {
        if (window.confirm("Are you sure you want to delete this item?")) {
            try {
                const config = { headers: { Authorization: `Bearer ${token}` } };
                await axios.delete(`${API_URL}/api/admin/menu/${id}`, config);
                fetchData();
            } catch (err) {
                alert("Failed to delete item");
            }
        }
    };

    const [editingItem, setEditingItem] = useState(null);
    const [showBillModal, setShowBillModal] = useState(false);
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showHistory, setShowHistory] = useState('live'); // 'live', 'unpaid', 'history', 'refunds'
    const [lastUpdated, setLastUpdated] = useState(null);
    const [updatingStatusId, setUpdatingStatusId] = useState(null);
    const [refundingId, setRefundingId] = useState(null);
    const [refundError, setRefundError] = useState('');

    const handleUpdateStatus = async (id, newStatus) => {
        setUpdatingStatusId(id);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.put(`${API_URL}/api/admin/orders/${id}/status`, { status: newStatus }, config);
            fetchData();
        } catch (err) {
            alert("Failed to update status");
        } finally {
            setUpdatingStatusId(null);
        }
    };

    const handleCheckRefundStatus = async (id) => {
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const res = await axios.get(`${API_URL}/api/admin/orders/${id}/refund-status`, config);
            const status = res.data.status;
            const details = res.data.data;
            alert(`Gateway Status: ${status}\n${JSON.stringify(details, null, 2)}`);
            fetchData();
        } catch (err) {
            alert(err.response?.data?.message || "Failed to check refund status");
        }
    };

    const handleManualRefund = async (order, reason = '') => {
        const confirmMsg = `Issue a FULL refund of ₹${order.total.toFixed(2)} for ${order.customer?.name}'s order?\n\nReason: ${reason || 'Manual admin refund'}\n\nThis will immediately call the Cashfree API. Proceed?`;
        if (!window.confirm(confirmMsg)) return;

        setRefundingId(order._id);
        setRefundError('');
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const res = await axios.post(
                `${API_URL}/api/admin/orders/${order._id}/manual-refund`,
                { amount: order.total, reason: reason || 'Manual admin refund' },
                config
            );
            alert(`✅ Refund initiated!\nRefund ID: ${res.data.refund_id}\nStatus: ${res.data.refund_status}\n\nThe customer will receive the money within 5-7 business days.`);
            setShowRefundModal(false);
            fetchData();
        } catch (err) {
            const msg = err.response?.data?.message || 'Refund failed. Please try again.';
            setRefundError(msg);
            alert(`❌ Refund failed: ${msg}`);
        } finally {
            setRefundingId(null);
        }
    };

    const handleVerifyAndRefund = async (order) => {
        const confirmMsg = `This will:\n1. Check Cashfree to see if ₹${order.total.toFixed(2)} was actually received\n2. If YES → issue a full refund immediately\n3. If NO → mark the order as 'Payment Failed'\n\nProceed for ${order.customer?.name}'s order?`;
        if (!window.confirm(confirmMsg)) return;

        setRefundingId(order._id);
        setRefundError('');
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const res = await axios.post(
                `${API_URL}/api/admin/orders/${order._id}/verify-and-refund`,
                { reason: 'Admin verified payment after status check failure' },
                config
            );
            alert(`✅ ${res.data.message}${res.data.paid ? `\n\nRefund ID: ${res.data.refund_id}\nStatus: ${res.data.refund_status}` : ''}`);
            fetchData();
        } catch (err) {
            const msg = err.response?.data?.message || 'Verification failed. Please try again.';
            setRefundError(msg);
            alert(`❌ ${msg}`);
        } finally {
            setRefundingId(null);
        }
    };

    const handleEditItem = (item) => {
        setEditingItem(item);
        setNewItem({
            name: item.name,
            price: item.price,
            description: item.description || '',
            shortDescription: item.shortDescription || '',
            image: toFullImageUrl(item.image || ''),
            category: item.category || 'Main',
            minQty: item.minQty || 1
        });
        setImageFile(null);
        setImagePreview(resolveImageUrl(item.image));
        setTimeout(() => {
            editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
    };

    const handleUpdateItem = async (e) => {
        e.preventDefault();
        console.log("Updating item...", editingItem);
        try {
            let imageUrl = newItem.image;

            // If user selected a new file, upload it first
            if (imageFile) {
                console.log("Uploading new image...");
                const uploadedUrl = await uploadImage();
                if (uploadedUrl) {
                    imageUrl = uploadedUrl;
                } else {
                    alert("Image upload failed, preventing item update.");
                    return;
                }
            }

            const config = { headers: { Authorization: `Bearer ${token}` } };
            const updateUrl = `${API_URL}/api/admin/menu/${editingItem._id}`;
            const payload = { ...newItem, image: toFullImageUrl(imageUrl) };

            console.log("Sending PUT request to:", updateUrl);
            console.log("Payload:", payload);

            await axios.put(updateUrl, payload, config);

            console.log("Update success!");
            setNewItem({ name: '', price: '', description: '', shortDescription: '', image: '', category: 'Main', minQty: 1 });
            setImageFile(null);
            setImagePreview('');
            setEditingItem(null);
            fetchData();
            alert("✅ Item updated successfully!");
        } catch (err) {
            console.error("Update failed:", err);
            alert(`Failed to update item: ${err.response?.data?.message || err.message}`);
        }
    };

    const cancelEdit = () => {
        setEditingItem(null);
        setNewItem({ name: '', price: '', description: '', shortDescription: '', image: '', category: 'Main', minQty: 1 });
        setImageFile(null);
        setImagePreview('');
    };

    const showBill = (order) => {
        setSelectedOrder(order);
        setShowBillModal(true);
    };

    const showRefund = (order) => {
        setSelectedOrder(order);
        setShowRefundModal(true);
    };

    const logout = () => {
        localStorage.removeItem('adminToken');
        navigate('/admin-login');
    };

    const isRefundRelated = (o) =>
        o.status === 'Refunding' ||
        o.status === 'Refunded' ||
        o.paymentStatus === 'Refunding' ||
        o.paymentStatus === 'Refunded' ||
        o.paymentStatus === 'Refund Failed';

    // Money actually collected. Anything else is a checkout attempt, not an order.
    const isPaid = (o) => o.paymentStatus === 'Paid';

    const isClosed = (o) =>
        o.status === 'Delivered' ||
        o.status === 'Fulfilled' ||
        o.status === 'Rejected';

    // The kitchen's work list: paid, still to be fulfilled.
    const activeOrders = orders.filter(o => !isRefundRelated(o) && !isClosed(o) && isPaid(o));

    // Failed or abandoned checkouts. Kept visible — a payment can succeed at the
    // gateway while the callback never reaches us, and this is where we'd spot it.
    const unpaidOrders = orders.filter(o => !isRefundRelated(o) && !isClosed(o) && !isPaid(o));

    const historyOrders = orders.filter(o => isClosed(o) || isRefundRelated(o));
    const refundOrders = orders.filter(isRefundRelated);

    let displayedOrders = activeOrders;
    if (showHistory === 'history') displayedOrders = historyOrders;
    else if (showHistory === 'refunds') displayedOrders = refundOrders;
    else if (showHistory === 'unpaid') displayedOrders = unpaidOrders;

    // Refunded orders are deliberately excluded — that money went back.
    const paidOrders = orders.filter(isPaid);

    const stats = [
        { label: 'Paid Orders', value: paidOrders.length, icon: ShoppingCart, color: 'from-blue-500 to-blue-600' },
        { label: 'Menu Items', value: menuItems.length, icon: Package, color: 'from-amber-500 to-amber-600' },
        { label: 'Revenue', value: `₹${paidOrders.reduce((sum, o) => sum + o.total, 0).toFixed(2)}`, icon: DollarSign, color: 'from-green-500 to-green-600' },
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-amber-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-3">
                            <img
                                src="/logo.png"
                                alt="Nayan's Kitchen Logo"
                                className="w-12 h-12 object-contain rounded-xl shadow-md border border-amber-100"
                            />
                            <div>
                                <h1 className="text-xl font-black text-gray-900 leading-none">Nayan's Kitchen & Delights</h1>
                                <p className="text-[10px] font-bold text-amber-600 uppercase mt-1">Admin Dashboard</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={logout}
                                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-xl font-semibold hover:bg-red-100 transition-colors"
                            >
                                <LogOut size={18} />
                                <span className="hidden sm:inline">Logout</span>
                            </button>
                            {/* Hamburger — mobile only */}
                            <button
                                onClick={() => setMobileNavOpen(o => !o)}
                                className="md:hidden p-2 rounded-lg hover:bg-amber-50 text-gray-700"
                            >
                                {mobileNavOpen ? <X size={24} /> : <MenuIcon size={24} />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Nav Dropdown */}
                {mobileNavOpen && (
                    <div className="md:hidden border-t border-amber-100 bg-white px-4 py-3 flex flex-col gap-1">
                        {[
                            { key: 'orders',    icon: ClipboardList, label: 'Orders' },
                            { key: 'menu',      icon: Package,       label: 'Menu Management' },
                            { key: 'finance',   icon: DollarSign,    label: 'Finance' },
                            { key: 'customers', icon: Users,         label: 'Customers' },
                            { key: 'messages',  icon: Mail,          label: 'Messages' },
                            { key: 'feedback',  icon: Star,          label: 'Feedback' },
                            { key: 'settings',  icon: Settings,      label: 'Settings' },
                        ].map(({ key, icon: Icon, label }) => (
                            <button
                                key={key}
                                onClick={() => { setActiveTab(key); setMobileNavOpen(false); }}
                                className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl font-semibold text-left transition-colors ${
                                    activeTab === key
                                        ? 'bg-amber-600 text-white'
                                        : 'text-gray-700 hover:bg-amber-50 hover:text-amber-700'
                                }`}
                            >
                                <Icon size={20} />
                                {label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-3 gap-3 sm:gap-6 mb-6 sm:mb-8">
                    {stats.map((stat, idx) => (
                        <div key={idx} className="bg-white rounded-2xl shadow-lg p-3 sm:p-6 border border-gray-100 hover:shadow-xl transition-shadow">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <div>
                                    <p className="text-gray-600 text-xs sm:text-sm font-semibold mb-1">{stat.label}</p>
                                    <p className="text-xl sm:text-3xl font-bold text-gray-900">{stat.value}</p>
                                </div>
                                <div className={`bg-gradient-to-br ${stat.color} p-2 sm:p-4 rounded-xl sm:rounded-2xl self-start sm:self-auto`}>
                                    <stat.icon className="text-white" size={20} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Tab Navigation — desktop only */}
                <div className="hidden md:block bg-white rounded-2xl shadow-lg border border-gray-100 mb-6">
                    <div className="overflow-x-auto scrollbar-hide">
                        <div className="flex border-b border-gray-200 min-w-max sm:min-w-0">
                            {[
                                { key: 'orders',   icon: ClipboardList, label: 'Orders' },
                                { key: 'menu',     icon: Package,       label: 'Menu' },
                                { key: 'finance',  icon: DollarSign,    label: 'Finance' },
                                { key: 'customers',icon: Users,         label: 'Customers' },
                                { key: 'messages', icon: Mail,          label: 'Messages' },
                                { key: 'feedback', icon: Star,          label: 'Feedback' },
                                { key: 'settings', icon: Settings,      label: 'Settings' },
                            ].map(({ key, icon: Icon, label }) => (
                                <button
                                    key={key}
                                    onClick={() => setActiveTab(key)}
                                    className={`flex items-center justify-center gap-1.5 px-4 sm:px-6 py-3 sm:py-4 font-semibold transition-colors whitespace-nowrap flex-1 sm:flex-1 text-sm ${
                                        activeTab === key
                                            ? 'text-amber-600 border-b-2 border-amber-600 bg-amber-50'
                                            : 'text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    <Icon size={18} />
                                    <span className="hidden sm:inline">{label}</span>
                                    <span className="inline sm:hidden text-[10px] font-bold leading-none mt-0.5 block">{label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Settings Tab (Configuration) */}
                {activeTab === 'settings' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6 items-start">
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                                <Settings className="text-amber-600" /> Payment Configuration
                            </h2>
                            <p className="text-gray-500 mb-6">Configure your Razorpay, PhonePe, or Paytm credentials here to enable seamless online payments.</p>
                            <PaymentConfigForm token={token} API_URL={API_URL} />
                        </div>

                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                                <Mail className="text-blue-500" /> Email Billing Configuration
                            </h2>
                            <p className="text-gray-500 mb-2">Configure an automated Email system to automatically send beautifully-formatted digital bills to your customers upon order fulfillment.</p>
                            <EmailConfigForm token={token} API_URL={API_URL} />
                        </div>

                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                                <MessageSquare className="text-green-600" /> Order Alert Configuration
                            </h2>
                            <p className="text-gray-500 mb-2">Get a WhatsApp message on your phone the instant a customer places an order, so nothing is missed during service.</p>
                            <AlertConfigForm token={token} API_URL={API_URL} />
                        </div>

                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                                <Key className="text-gray-500" /> Setting Up WhatsApp Cloud API
                            </h2>
                            <p className="text-gray-500 mb-6">A one-time setup on Meta's developer console. No business verification needed while you're on the free test sender.</p>

                            <ol className="space-y-4">
                                {[
                                    <>Create an app at <span className="font-bold text-gray-900">developers.facebook.com</span> and add the <span className="font-bold text-gray-900">WhatsApp</span> product to it.</>,
                                    <>Open <span className="font-bold text-gray-900">WhatsApp → API Setup</span>. Copy the <span className="font-bold text-gray-900">Phone number ID</span> and the access token into the form beside this.</>,
                                    <>On that same page, add your alert number under <span className="font-bold text-gray-900">"To"</span> and <span className="font-bold text-gray-900">verify it by code</span>. The test sender only delivers to numbers listed there (max 5).</>,
                                    <>Hit <span className="font-bold text-gray-900">Test</span>. If the message arrives, you're done — orders will alert the same way.</>,
                                    <>Finally, swap the 24-hour token for a permanent one: <span className="font-bold text-gray-900">Business Settings → System Users</span> → generate a token with <span className="font-mono text-xs text-gray-700">whatsapp_business_messaging</span>.</>
                                ].map((step, idx) => (
                                    <li key={idx} className="flex gap-3 text-sm text-gray-600">
                                        <span className="shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 font-bold flex items-center justify-center text-xs">{idx + 1}</span>
                                        <span className="pt-0.5">{step}</span>
                                    </li>
                                ))}
                            </ol>

                            <a
                                href="https://developers.facebook.com/apps"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-6 w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 rounded-xl font-bold hover:shadow-lg transition-all flex justify-center items-center gap-2"
                            >
                                <MessageSquare size={20} />
                                Open Meta Developer Console
                            </a>

                            <div className="mt-4 bg-gray-50 border border-gray-100 rounded-xl p-4">
                                <p className="font-bold text-gray-700 text-xs mb-2">The template body</p>
                                <p className="text-xs text-gray-500 mb-3">
                                    Create a <span className="font-bold text-gray-700">Utility</span> template named <span className="font-mono text-gray-700">order_alert</span> in English (<span className="font-mono text-gray-700">en</span>) with this exact body. It needs all six variables in this order — Meta rejects a send whose parameter count differs.
                                </p>
                                <pre className="text-[11px] leading-relaxed bg-white border border-gray-200 rounded-lg p-3 overflow-x-auto text-gray-700 whitespace-pre-wrap">{`*New Order Received* 🛎

*Order:* {{1}}
*Customer:* {{2}}
*Phone:* {{3}}
*Items:* {{4}}
*Total:* {{5}}
*Address:* {{6}}

Open the admin dashboard to accept and start preparing.`}</pre>
                                <p className="text-xs text-gray-500 mt-3">
                                    Used only when the free 24-hour window has closed, billing a few paise per message. Test-sender messages are free. Leave the template name blank above to never incur charges.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Customers Tab */}
                {activeTab === 'customers' && (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                        <div className="bg-amber-600 p-6 text-white">
                            <h2 className="text-2xl font-bold">Registered Customers</h2>
                            <p className="text-amber-100">{customers.length} total registered accounts</p>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Name</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Email</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Phone</th>
                                        <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Joined Date</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {customers.map(customer => (
                                        <tr key={customer._id} className="hover:bg-amber-50">
                                            <td className="px-6 py-4 font-bold text-gray-900">{customer.name}</td>
                                            <td className="px-6 py-4 text-gray-600">{customer.email}</td>
                                            <td className="px-6 py-4 text-gray-600">{customer.phone}</td>
                                            <td className="px-6 py-4 text-gray-500">{new Date(customer.createdAt).toLocaleDateString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Support Messages Tab */}
                {activeTab === 'messages' && (
                    <div className="space-y-6">
                        {supportMessages.length === 0 ? (
                            <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-lg">
                                <Mail className="mx-auto text-gray-200 mb-4" size={64} />
                                <p className="text-gray-500 font-bold text-xl">No support messages found</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-4">
                                {supportMessages.map(msg => (
                                    <div key={msg._id} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 flex flex-col md:flex-row justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${msg.status === 'Resolved' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                                    {msg.status}
                                                </span>
                                                <span className="text-xs text-gray-400 font-bold uppercase">{msg.subject}</span>
                                                <span className="text-xs text-gray-400">{new Date(msg.createdAt).toLocaleString()}</span>
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-900 mb-1">{msg.name}</h3>
                                            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                                                <span className="flex items-center gap-1"><Mail size={14} /> {msg.email}</span>
                                                <span className="flex items-center gap-1"><Phone size={14} /> {msg.phone}</span>
                                            </div>
                                            <p className="bg-gray-50 p-4 rounded-xl text-gray-700 italic border border-gray-100">"{msg.message}"</p>
                                        </div>
                                        <div className="flex md:flex-col justify-end gap-2">
                                            {msg.status === 'Pending' && (
                                                <button
                                                    onClick={async () => {
                                                        const config = { headers: { Authorization: `Bearer ${token}` } };
                                                        await axios.put(`${API_URL}/api/admin/support-messages/${msg._id}`, { status: 'Resolved' }, config);
                                                        fetchData();
                                                    }}
                                                    className="bg-green-600 text-white px-4 py-2 rounded-xl font-bold hover:bg-green-700 transition-all flex items-center gap-2"
                                                >
                                                    <Check size={18} /> Resolve
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Feedback Tab */}
                {activeTab === 'feedback' && (
                    <div className="space-y-6">
                        {feedbacks.length === 0 ? (
                            <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-lg">
                                <Star className="mx-auto text-gray-200 mb-4" size={64} />
                                <p className="text-gray-500 font-bold text-xl">No feedback received yet</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {feedbacks.map(fb => (
                                    <div key={fb._id} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                        <div className="flex justify-between items-start mb-4">
                                            <div>
                                                <h3 className="font-bold text-gray-900 text-lg">{fb.customerName || 'Anonymous'}</h3>
                                                <p className="text-xs text-gray-400">Order ID: {fb.orderId ? `#${fb.orderId.slice(-6).toUpperCase()}` : 'N/A'}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xs text-gray-400">{new Date(fb.createdAt).toLocaleDateString()}</p>
                                            </div>
                                        </div>
                                        <div className="space-y-3 mb-4">
                                            {fb.ratings.map((r, idx) => (
                                                <div key={idx} className="flex justify-between items-center bg-amber-50 p-2 rounded-lg">
                                                    <span className="text-sm font-bold text-gray-700">{r.itemName}</span>
                                                    <div className="flex items-center gap-1">
                                                        <Star size={14} className="fill-amber-500 text-amber-500" />
                                                        <span className="text-sm font-black text-amber-700">{r.rating}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        {fb.comment && (
                                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                                <p className="text-gray-600 text-sm italic">"{fb.comment}"</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Orders Tab */}
                {activeTab === 'orders' && (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                        <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-6 flex flex-col md:flex-row justify-between items-center gap-4">
                            <div>
                                <h2 className="text-2xl font-bold text-white">
                                    {showHistory === 'history' ? 'Order History' : showHistory === 'refunds' ? 'Refunded Orders' : showHistory === 'unpaid' ? 'Unpaid Orders' : 'Live Orders'}
                                </h2>
                                <p className="text-amber-100 text-sm mt-1">
                                    {showHistory === 'history' ? 'View past delivered and rejected orders'
                                        : showHistory === 'refunds' ? 'Manage and track payment reversals'
                                        : showHistory === 'unpaid' ? 'Checkouts that never completed — verify against your gateway before dismissing'
                                        : 'Paid orders awaiting fulfilment · T+1 delivery schedule'}
                                </p>
                            </div>
                            <div className="bg-black/20 p-1 rounded-xl flex gap-1 transform transition-all">
                                <button
                                    onClick={() => setShowHistory('live')}
                                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-all duration-200 ${showHistory === 'live'
                                        ? 'bg-white text-amber-600 shadow-lg scale-105'
                                        : 'text-amber-100 hover:bg-white/10'
                                        }`}
                                >
                                    Live Queue
                                </button>
                                <button
                                    onClick={() => setShowHistory('history')}
                                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-all duration-200 ${showHistory === 'history'
                                        ? 'bg-white text-amber-600 shadow-lg scale-105'
                                        : 'text-amber-100 hover:bg-white/10'
                                        }`}
                                >
                                    History
                                </button>
                                <button
                                    onClick={() => setShowHistory('unpaid')}
                                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-all duration-200 flex items-center gap-2 ${showHistory === 'unpaid'
                                        ? 'bg-white text-gray-600 shadow-lg scale-105'
                                        : 'text-amber-100 hover:bg-white/10'
                                        }`}
                                >
                                    Unpaid
                                    {unpaidOrders.length > 0 && (
                                        <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-black ${showHistory === 'unpaid' ? 'bg-gray-200 text-gray-700' : 'bg-white/25 text-white'}`}>
                                            {unpaidOrders.length}
                                        </span>
                                    )}
                                </button>
                                <button
                                    onClick={() => setShowHistory('refunds')}
                                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-all duration-200 ${showHistory === 'refunds'
                                        ? 'bg-white text-rose-500 shadow-lg scale-105'
                                        : 'text-amber-100 hover:bg-white/10'
                                        }`}
                                >
                                    Refunds
                                </button>
                                <div className="mx-1 h-6 w-[1px] bg-white/20 my-auto" />
                                <button
                                    onClick={() => fetchData()}
                                    disabled={loading}
                                    className={`p-2 rounded-lg text-amber-100 hover:bg-white/20 transition-all ${loading ? 'animate-spin' : ''}`}
                                    title={lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()} · auto-refreshes every 30s` : 'Refresh Data'}
                                >
                                    <RefreshCw size={18} />
                                </button>
                            </div>
                        </div>

                        {loading ? (
                            <div className="p-12 text-center">
                                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
                                <p className="text-gray-500 mt-4">Loading orders...</p>
                            </div>
                        ) : displayedOrders.length === 0 ? (
                            <div className="p-12 text-center">
                                <ClipboardList className="mx-auto text-gray-300 mb-4" size={48} />
                                <p className="text-gray-500">No {showHistory === 'history' ? 'past' : showHistory === 'refunds' ? 'refunded' : showHistory === 'unpaid' ? 'unpaid' : 'active'} orders found</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                                        {showHistory === 'refunds' ? (
                                            <tr>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Customer Info</th>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Order Date</th>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Total Price</th>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Payment/PG</th>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Refund ID</th>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Refund Status</th>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Actions</th>
                                            </tr>
                                        ) : (
                                            <tr>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Customer</th>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Address</th>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Pin Code</th>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Order Date</th>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Total</th>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Instructions</th>
                                                <th className="px-6 py-4 text-left text-sm font-bold text-gray-700">Actions</th>
                                            </tr>
                                        )}
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {displayedOrders.map(order =>
                                            showHistory === 'refunds' ? (
                                                <tr key={order._id} className="hover:bg-amber-50 transition-colors">
                                                    <td className="px-6 py-4">
                                                        <div className="font-bold text-gray-900">{order.customer?.name}</div>
                                                        <div className="text-sm text-gray-500">{order.customer?.phone}</div>
                                                        <div className="text-xs text-gray-400">{order.customer?.email}</div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-600">
                                                        {new Date(order.orderDate).toLocaleString()}
                                                    </td>
                                                    <td className="px-6 py-4 font-bold text-gray-900">
                                                        ₹{order.total}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                            {order.paymentMethod || 'Unknown PG'}
                                                        </span>
                                                        {order.transactionId && (
                                                            <div className="text-xs text-gray-400 mt-1 font-mono break-all">{order.transactionId}</div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {order.refundId ? (
                                                            <div className="text-xs text-amber-700 font-mono bg-amber-50 p-1 rounded border border-amber-100 break-all">
                                                                {order.refundId}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-400 text-xs italic">Not Generated</span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold 
                                                            ${order.status === 'Refunded' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                                                            {order.status === 'Fulfilled' ? 'Delivered' : order.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => showRefund(order)}
                                                                className="flex items-center justify-center p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                                                                title="View Refund Details"
                                                            >
                                                                <ClipboardList size={18} />
                                                            </button>
                                                            {order.refundId && (
                                                                <button
                                                                    onClick={() => handleCheckRefundStatus(order._id)}
                                                                    className="flex items-center justify-center p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                                                                    title="Sync Refund Status"
                                                                >
                                                                    <RefreshCw size={18} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                <tr key={order._id} className="hover:bg-amber-50 transition-colors">
                                                    <td className="px-6 py-4">
                                                        <div className="font-bold text-gray-900">{order.customer?.name}</div>
                                                        <div className="text-sm text-gray-500">{order.customer?.phone}</div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                                                        {order.customer?.address}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                                                        {order.customer?.pincode}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold
                                                            ${showHistory === 'unpaid' ? 'bg-gray-100 text-gray-600' :
                                                                showHistory === 'history' ? (order.status === 'Rejected' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600') :
                                                                    (order.status === 'Accepted' ? 'bg-indigo-100 text-indigo-700' : 'bg-blue-100 text-blue-700')}`}>
                                                            {showHistory === 'unpaid'
                                                                ? (order.paymentStatus || 'Pending')
                                                                : order.status === 'Confirmed' ? 'Pending' : (order.status === 'Fulfilled' ? 'Delivered' : (order.status || 'Pending'))}
                                                        </span>
                                                        <br />
                                                        <span className="text-xs text-gray-500 mt-1 block">
                                                            {new Date(order.orderDate).toLocaleDateString()}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 font-bold text-green-600 text-lg">
                                                        ₹{order.total}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm italic text-gray-500 max-w-xs truncate">
                                                        {order.specialInstructions || "None"}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => showBill(order)}
                                                                className="flex items-center justify-center p-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                                                                title="View Bill Details"
                                                            >
                                                                <ClipboardList size={18} />
                                                            </button>
                                                            {showHistory === 'live' && (!order.status || order.status === 'Pending' || order.status === 'Confirmed') && (
                                                                <>
                                                                    <button
                                                                        onClick={() => handleUpdateStatus(order._id, 'Accepted')}
                                                                        disabled={updatingStatusId === order._id}
                                                                        className={`flex items-center justify-center p-2 rounded-lg transition-colors ${updatingStatusId === order._id ? 'bg-gray-400 cursor-not-allowed' : 'bg-emerald-500 text-white hover:bg-emerald-600'}`}
                                                                        title="Accept Order"
                                                                    >
                                                                        {updatingStatusId === order._id ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Check size={18} />}
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleUpdateStatus(order._id, 'Rejected')}
                                                                        disabled={updatingStatusId === order._id}
                                                                        className={`flex items-center justify-center p-2 rounded-lg transition-colors ${updatingStatusId === order._id ? 'bg-gray-400 cursor-not-allowed' : 'bg-rose-500 text-white hover:bg-rose-600'}`}
                                                                        title="Reject Order"
                                                                    >
                                                                        {updatingStatusId === order._id ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <X size={18} />}
                                                                    </button>
                                                                </>
                                                            )}
                                                            {showHistory === 'live' && order.status === 'Accepted' && (
                                                                <button
                                                                    onClick={() => handleUpdateStatus(order._id, 'Delivered')}
                                                                    disabled={updatingStatusId === order._id}
                                                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg font-semibold transition-colors text-sm ${updatingStatusId === order._id ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
                                                                >
                                                                    {updatingStatusId === order._id ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Truck size={16} />}
                                                                    {updatingStatusId === order._id ? 'Processing...' : 'Send Bill & Mark Delivered'}
                                                                </button>
                                                            )}
                                                            {/* Verify & Refund: for Cashfree orders that never settled. Also covers
                                                                'Abandoned', since a late gateway callback is exactly what this checks for. */}
                                                            {order.paymentMethod === 'Cashfree' && (order.status === 'Pending Payment' || ['Pending', 'Abandoned', 'Not Paid', 'Failed', 'Dropped'].includes(order.paymentStatus)) && (
                                                                <button
                                                                    onClick={() => handleVerifyAndRefund(order)}
                                                                    disabled={refundingId === order._id}
                                                                    className={`flex items-center gap-1 px-2 py-2 rounded-lg text-xs font-bold transition-colors border ${refundingId === order._id
                                                                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                                        : 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100'
                                                                        }`}
                                                                    title="Verify if payment was received, then refund"
                                                                >
                                                                    {refundingId === order._id
                                                                        ? <div className="animate-spin h-3 w-3 border-2 border-orange-500 border-t-transparent rounded-full" />
                                                                        : <RotateCcw size={14} />}
                                                                    Verify & Refund
                                                                </button>
                                                            )}
                                                            {/* Manual refund button for Cashfree paid orders */}
                                                            {order.paymentMethod === 'Cashfree' && (order.paymentStatus === 'Paid') && order.status !== 'Refunded' && order.status !== 'Refunding' && (
                                                                <button
                                                                    onClick={() => { setSelectedOrder(order); setShowRefundModal(true); }}
                                                                    disabled={refundingId === order._id}
                                                                    className="flex items-center justify-center p-2 bg-rose-50 text-rose-600 rounded-lg hover:bg-rose-100 transition-colors border border-rose-200"
                                                                    title="Issue Refund"
                                                                >
                                                                    <RotateCcw size={16} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Menu Management Tab */}
                {activeTab === 'menu' && (
                    <div className="space-y-6">
                        {/* Minimum Order Value Setting */}
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                            <h2 className="text-lg font-bold text-gray-900 mb-1">Minimum Order Value</h2>
                            <p className="text-sm text-gray-500 mb-4">Customers cannot place an order below this amount. Set to 0 to disable.</p>
                            <div className="flex items-center gap-3">
                                <div className="relative flex-1 max-w-xs">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">₹</span>
                                    <input
                                        type="number"
                                        min="0"
                                        value={minOrderInput}
                                        onChange={e => setMinOrderInput(e.target.value)}
                                        className="w-full pl-8 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none text-lg font-semibold"
                                        placeholder="0"
                                    />
                                </div>
                                <button
                                    onClick={handleSaveMinOrderValue}
                                    disabled={minOrderSaving}
                                    className="px-5 py-2.5 bg-amber-600 text-white font-bold rounded-xl hover:bg-amber-700 transition-all disabled:bg-gray-400"
                                >
                                    {minOrderSaving ? 'Saving...' : 'Save'}
                                </button>
                                {minOrderSaved && <span className="text-green-600 font-semibold text-sm">✓ Saved!</span>}
                            </div>
                            {minOrderValue > 0 && (
                                <p className="text-xs text-amber-700 mt-2 font-medium">Currently active: customers must order at least ₹{minOrderValue}</p>
                            )}
                        </div>

                        {/* Menu Items List */}
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                            <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-6">
                                <h2 className="text-2xl font-bold text-white">Current Menu Items</h2>
                                <p className="text-amber-100 text-sm mt-1">{menuItems.length} items available</p>
                            </div>

                            {loading ? (
                                <div className="p-12 text-center">
                                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
                                    <p className="text-gray-500 mt-4">Loading menu...</p>
                                </div>
                            ) : menuItems.length === 0 ? (
                                <div className="p-12 text-center">
                                    <Package className="mx-auto text-gray-300 mb-4" size={48} />
                                    <p className="text-gray-500">No menu items yet. Add your first item above!</p>
                                </div>
                            ) : (
                                <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {menuItems.map(item => (
                                        <div key={item._id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-amber-500 hover:shadow-lg transition-all group flex flex-col">
                                            <div className="relative h-48 overflow-hidden">
                                                <img
                                                    src={resolveImageUrl(item.image)}
                                                    alt={item.name}
                                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                                />
                                            </div>

                                            <div className="p-4 flex flex-col flex-1">
                                                <div className="flex justify-between items-start mb-2">
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="font-bold text-gray-900 text-lg line-clamp-1" title={item.name}>{item.name}</h3>
                                                    </div>
                                                    <div className="text-right ml-4">
                                                        <p className="text-amber-600 font-black text-lg whitespace-nowrap">₹{item.price}</p>
                                                        {item.shortDescription && (
                                                            <p className="text-xs font-bold text-gray-500 italic whitespace-nowrap">{item.shortDescription}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="mt-2 flex-1">
                                                    {item.description && (
                                                        <p className="text-sm text-gray-500 mb-2 line-clamp-2" title={item.description}>
                                                            {item.description}
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                                                    <button
                                                        onClick={() => handleEditItem(item)}
                                                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg font-semibold hover:bg-blue-100 transition-colors text-sm"
                                                    >
                                                        <Edit2 size={16} />
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteItem(item._id)}
                                                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg font-semibold hover:bg-red-100 transition-colors text-sm"
                                                    >
                                                        <Trash2 size={16} />
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Add/Edit Item Form */}
                        <div ref={editFormRef} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                            <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-6">
                                <div className="flex justify-between items-center">
                                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                                        {editingItem ? <Edit2 size={28} /> : <PlusCircle size={28} />}
                                        {editingItem ? 'Edit Menu Item' : 'Add New Menu Item'}
                                    </h2>
                                    {editingItem && (
                                        <button
                                            type="button"
                                            onClick={cancelEdit}
                                            className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                                        >
                                            Cancel Edit
                                        </button>
                                    )}
                                </div>
                            </div>
                            <form onSubmit={editingItem ? handleUpdateItem : handleAddFood} className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    {/* Left Column: Name & Category */}
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Item Name</label>
                                            <input
                                                placeholder="e.g., Margherita Pizza"
                                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                                value={newItem.name}
                                                onChange={e => setNewItem({ ...newItem, name: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                                            <select
                                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                                value={newItem.category}
                                                onChange={e => setNewItem({ ...newItem, category: e.target.value })}
                                            >
                                                <option>Main</option>
                                                <option>Appetizer</option>
                                                <option>Dessert</option>
                                                <option>Beverage</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Right Column: Price, MinQty & Short Description */}
                                    <div className="space-y-4">
                                        {/* Price + MinQty side by side */}
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Price (₹)</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="299"
                                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                                    value={newItem.price}
                                                    onChange={e => setNewItem({ ...newItem, price: e.target.value })}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Min. Order Qty</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    placeholder="1"
                                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                                    value={newItem.minQty || 1}
                                                    onChange={e => setNewItem({ ...newItem, minQty: parseInt(e.target.value) || 1 })}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">Short Description (e.g., 6 pieces, 100 gram)</label>
                                            <input
                                                placeholder="Add a quick highlight..."
                                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                                value={newItem.shortDescription || ''}
                                                onChange={e => setNewItem({ ...newItem, shortDescription: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                                    <textarea
                                        placeholder="Enter item description..."
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all resize-none"
                                        style={{ minHeight: '80px', overflow: 'hidden' }}
                                        value={newItem.description || ''}
                                        ref={el => {
                                            if (el) {
                                                el.style.height = 'auto';
                                                el.style.height = el.scrollHeight + 'px';
                                            }
                                        }}
                                        onChange={e => {
                                            setNewItem({ ...newItem, description: e.target.value });
                                            e.target.style.height = 'auto';
                                            e.target.style.height = e.target.scrollHeight + 'px';
                                        }}
                                        rows={3}
                                    />
                                </div>

                                <div className="mb-6">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">Menu Item Image</label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-2">Upload Image File</label>
                                            <div className="relative">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleImageFileChange}
                                                    className="hidden"
                                                    id="imageUpload"
                                                />
                                                <label
                                                    htmlFor="imageUpload"
                                                    className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-amber-300 rounded-xl hover:border-amber-500 hover:bg-amber-50 cursor-pointer transition-all"
                                                >
                                                    <Upload size={20} className="text-amber-600" />
                                                    <span className="text-sm font-medium text-gray-700">
                                                        {imageFile ? imageFile.name : 'Choose Image File'}
                                                    </span>
                                                </label>
                                            </div>
                                            {imagePreview && (
                                                <div className="mt-3">
                                                    <img
                                                        src={imagePreview}
                                                        alt="Preview"
                                                        className="w-full h-32 object-cover rounded-lg border-2 border-amber-200"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setImageFile(null);
                                                            setImagePreview('');
                                                        }}
                                                        className="mt-2 text-sm text-red-600 hover:text-red-700 font-medium"
                                                    >
                                                        Remove Image
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-2">Or Enter Image URL</label>
                                            <input
                                                placeholder="https://example.com/image.jpg"
                                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                                value={newItem.image}
                                                onChange={e => setNewItem({ ...newItem, image: e.target.value })}
                                                disabled={!!imageFile}
                                            />
                                            {imageFile && (
                                                <p className="mt-2 text-xs text-gray-500">URL input disabled when file is selected</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    type="submit"
                                    disabled={uploading}
                                    className={`w-full bg-gradient-to-r from-amber-600 to-amber-500 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {uploading ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                            Uploading Image...
                                        </span>
                                    ) : (
                                        editingItem ? 'Update Item' : 'Add Item to Menu'
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                )}

                {/* Finance ERP Tab */}
                {activeTab === 'finance' && (
                    <div className="space-y-6">
                        <FinanceDashboard embedded={true} />
                    </div>
                )}
            </div>

            {/* Bill Modal */}
            {showBillModal && selectedOrder && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
                    <div id="printable-bill" className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slideUp">
                        <div className="sticky top-0 bg-gradient-to-r from-amber-600 to-amber-700 text-white p-6 rounded-t-3xl flex justify-between items-center z-10 print:bg-white print:text-black">
                            <div className="flex items-center gap-3">
                                <ChefHat size={32} className="text-white print:text-amber-600" />
                                <div>
                                    <h2 className="text-2xl font-bold">Nayan's Kitchen & Delights</h2>
                                    <p className="text-amber-100 text-sm print:text-gray-500">Taste That Touches the Soul</p>
                                </div>
                            </div>
                            <button onClick={() => setShowBillModal(false)} className="hover:bg-white/20 p-2 rounded-full transition-colors print:hidden">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="p-6">
                            {/* Customer Info */}
                            <div className="bg-amber-50 rounded-2xl p-6 mb-6 border-2 border-amber-200">
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Customer Details</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-gray-600">Name</p>
                                        <p className="font-bold text-gray-900">{selectedOrder.customer?.name}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">Phone</p>
                                        <p className="font-bold text-gray-900">{selectedOrder.customer?.phone}</p>
                                    </div>
                                    <div className="col-span-2">
                                        <p className="text-sm text-gray-600">Email</p>
                                        <p className="font-bold text-gray-900">{selectedOrder.customer?.email}</p>
                                    </div>
                                    <div className="col-span-2">
                                        <p className="text-sm text-gray-600">Delivery Address</p>
                                        <p className="font-bold text-gray-900">{selectedOrder.customer?.address}</p>
                                    </div>
                                    <div className="col-span-2">
                                        <p className="text-sm text-gray-600">Pin Code</p>
                                        <p className="font-bold text-gray-900">{selectedOrder.customer?.pincode || 'N/A'}</p>
                                    </div>
                                    {selectedOrder.deliveryMethod !== 'Pickup' && (
                                        <div className="col-span-2">
                                            <p className="text-sm text-gray-600">Delivery Time Slot</p>
                                            <p className="font-bold text-gray-900">{selectedOrder.deliveryTimeSlot || 'N/A'}</p>
                                        </div>
                                    )}
                                    <div className="col-span-2">
                                        <p className="text-sm text-gray-600">Order Date</p>
                                        <p className="font-bold text-gray-900">{new Date(selectedOrder.orderDate).toLocaleString()}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Order Items */}
                            <div className="mb-6">
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Order Items</h3>
                                <div className="space-y-3">
                                    {selectedOrder.items?.map((item, index) => {
                                        // Use snapshot data (item.price/name) if available (for new orders), 
                                        // or fallback to populated menuItem data (for old orders)
                                        const price = item.price !== undefined ? item.price : (item.menuItem?.price || 0);
                                        const name = item.name || item.menuItem?.name || 'Item Deleted';
                                        const qty = item.quantity || 0;
                                        const itemTotal = price * qty;

                                        return (
                                            <div key={index} className="flex justify-between items-center bg-gray-50 p-4 rounded-xl border-2 border-gray-100">
                                                <div className="flex-1">
                                                    <p className="font-bold text-gray-900">{name}</p>
                                                    <p className="text-sm text-gray-600">Quantity: {qty}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-bold text-amber-700">₹{itemTotal.toFixed(2)}</p>
                                                    <p className="text-xs text-gray-500">₹{price} each</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Special Instructions */}
                            {selectedOrder.specialInstructions && (
                                <div className="bg-blue-50 rounded-2xl p-4 mb-6 border-2 border-blue-200">
                                    <p className="text-sm font-semibold text-blue-900 mb-1">Special Instructions</p>
                                    <p className="text-blue-800 italic">{selectedOrder.specialInstructions}</p>
                                </div>
                            )}

                            {/* Total */}
                            <div className="bg-gradient-to-br from-amber-50 to-orange-100 p-6 rounded-2xl border-2 border-amber-300">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-gray-700 font-semibold">Subtotal</span>
                                    <span className="text-gray-900 font-bold text-lg">₹{selectedOrder.total.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between items-center mb-4">
                                    <span className="text-gray-700 font-semibold">Delivery</span>
                                    <span className="text-green-600 font-bold">FREE</span>
                                </div>
                                <div className="border-t-2 border-amber-400 pt-4 flex justify-between items-center">
                                    <span className="text-xl font-bold text-gray-900">Grand Total</span>
                                    <span className="text-3xl font-bold text-amber-700">₹{selectedOrder.total.toFixed(2)}</span>
                                </div>
                            </div>

                            {/* Print Button */}
                            {/* Action Buttons */}
                            <div className="mt-8 flex gap-4 print:hidden">
                                <button
                                    onClick={() => setShowBillModal(false)}
                                    className="flex-1 px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-bold text-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => window.print()}
                                    className="flex-1 px-6 py-4 bg-gradient-to-r from-amber-600 to-amber-700 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                                >
                                    Print Bill
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Refund Modal */}
            {showRefundModal && selectedOrder && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
                    <div id="printable-refund" className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slideUp">
                        <div className="sticky top-0 bg-gradient-to-r from-rose-600 to-rose-700 text-white p-6 rounded-t-3xl flex justify-between items-center z-10 print:bg-white print:text-black">
                            <div className="flex items-center gap-3">
                                <ClipboardList size={32} className="text-white print:text-rose-600" />
                                <div>
                                    <h2 className="text-2xl font-bold">Refund Details</h2>
                                    <p className="text-rose-100 text-sm print:text-gray-500">Nayan's Kitchen & Delights</p>
                                </div>
                            </div>
                            <button onClick={() => setShowRefundModal(false)} className="hover:bg-white/20 p-2 rounded-full transition-colors print:hidden">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="p-6">
                            {/* Order Info */}
                            <div className="bg-rose-50 rounded-2xl p-6 mb-6 border-2 border-rose-200">
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Refund Information</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-gray-600">Customer Name</p>
                                        <p className="font-bold text-gray-900">{selectedOrder.customer?.name}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600">Order Total</p>
                                        <p className="font-bold text-rose-700">₹{selectedOrder.total.toFixed(2)}</p>
                                    </div>
                                    <div className="col-span-2">
                                        <p className="text-sm text-gray-600">Order Date</p>
                                        <p className="font-bold text-gray-900">{new Date(selectedOrder.orderDate).toLocaleString()}</p>
                                    </div>
                                    <div className="col-span-2">
                                        <p className="text-sm text-gray-600">Payment Gateway</p>
                                        <p className="font-bold text-gray-900">{selectedOrder.paymentMethod}</p>
                                    </div>
                                    <div className="col-span-2">
                                        <p className="text-sm text-gray-600">Transaction ID</p>
                                        <p className="font-bold text-gray-900 font-mono break-all">{selectedOrder.transactionId}</p>
                                    </div>
                                    <div className="col-span-2">
                                        <p className="text-sm text-gray-600">Refund ID</p>
                                        <p className="font-bold text-gray-900 font-mono bg-white p-2 border border-gray-200 rounded mt-1 break-all">{selectedOrder.refundId}</p>
                                    </div>
                                    {selectedOrder.refundArn && (
                                        <div className="col-span-2">
                                            <p className="text-sm text-gray-600">Refund ARN (Bank Reference)</p>
                                            <p className="font-bold text-blue-700 font-mono bg-blue-50 p-2 border border-blue-200 rounded mt-1 break-all">
                                                {selectedOrder.refundArn}
                                            </p>
                                        </div>
                                    )}
                                    <div className="col-span-2">
                                        <p className="text-sm text-gray-600">Refund Created At</p>
                                        <p className="font-bold text-gray-900">
                                            {selectedOrder.refundDate ? new Date(selectedOrder.refundDate).toLocaleString() : <span className="text-gray-400 italic">Not Recorded (Historical)</span>}
                                        </p>
                                    </div>
                                    <div className="col-span-2">
                                        <p className="text-sm text-gray-600">Refund Processed At</p>
                                        <p className="font-bold text-gray-900">
                                            {selectedOrder.refundProcessedDate ? new Date(selectedOrder.refundProcessedDate).toLocaleString() : <span className="text-gray-400 italic">Not Recorded / Pending</span>}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="mt-8 flex gap-4 print:hidden">
                                <button
                                    onClick={() => setShowRefundModal(false)}
                                    className="flex-1 px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-bold text-lg hover:bg-gray-200 transition-colors"
                                >
                                    Close
                                </button>
                                {selectedOrder?.paymentMethod === 'Cashfree' && selectedOrder?.paymentStatus !== 'Refunded' && selectedOrder?.status !== 'Refunding' && (
                                    <button
                                        onClick={() => handleManualRefund(selectedOrder, 'Manual refund from admin panel')}
                                        disabled={refundingId === selectedOrder?._id}
                                        className={`flex-1 px-6 py-4 rounded-xl font-bold text-lg shadow-lg transition-all flex items-center justify-center gap-2 ${refundingId === selectedOrder?._id
                                            ? 'bg-gray-400 text-white cursor-not-allowed'
                                            : 'bg-gradient-to-r from-rose-600 to-rose-700 text-white hover:shadow-xl hover:scale-105'
                                            }`}
                                    >
                                        {refundingId === selectedOrder?._id
                                            ? <><div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" /> Processing...</>
                                            : <><RotateCcw size={20} /> Issue Full Refund</>}
                                    </button>
                                )}
                                <button
                                    onClick={() => window.print()}
                                    className="flex-1 px-6 py-4 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                                >
                                    Print Details
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
